import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/lib/stores/auth';
import { useNotificationsStore } from '@/lib/stores/notifications';
import { useStreamsStore } from '@/lib/stores/streams';
import { api } from '@/lib/api/client';

interface NotificationsProviderProps {
  children: React.ReactNode;
}

export function NotificationsProvider({ children }: NotificationsProviderProps) {
  const { fetchUnreadCount, fetchPreferences, connectToNotifications, disconnectFromNotifications } = useNotificationsStore();
  const { connectToStreamsRealtime, disconnectFromStreamsRealtime } = useStreamsStore();
  const { isInitialized: isReady, isAuthenticated, user } = useAuthStore();
  const initializedForUserRef = useRef<string | null>(null);
  const userKey = user?.id ? String(user.id) : user?.email || null;

  useEffect(() => {
    if ('Notification' in window) {
      useNotificationsStore.setState({ pushPermission: Notification.permission });
    }
  }, []);

  useEffect(() => {
    const initializeNotifications = async () => {
      if (!isReady || !isAuthenticated || !user) {
        // Disconnect on logout
        disconnectFromNotifications();
        disconnectFromStreamsRealtime();
        initializedForUserRef.current = null;
        return;
      }

      if (api.isBackendUnavailable()) {
        return;
      }

      if (initializedForUserRef.current === userKey) {
        return;
      }

      try {
        await Promise.all([
          fetchUnreadCount(),
          fetchPreferences(),
          connectToNotifications(),
          connectToStreamsRealtime(),
        ]);
        initializedForUserRef.current = userKey;
      } catch {
        // Keep the provider quiet if the backend is down; the API client handles the fallback state.
      }
    };

    const timeoutId = setTimeout(initializeNotifications, 100);
    return () => clearTimeout(timeoutId);
  }, [fetchUnreadCount, fetchPreferences, connectToNotifications, disconnectFromNotifications, connectToStreamsRealtime, disconnectFromStreamsRealtime, isReady, isAuthenticated, user, userKey]);

  return children;
}
